import { useEffect, useId, useRef, useState } from 'react';
import { Check, Copy, LogOut, Wallet } from 'lucide-react';
import type { Account } from '../auth/AccountContext';
import { ProfileGlyph } from './icons';

export function AccountMenu({ account }: { account: Account }) {
  const [open, setOpen] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>(
    'idle',
  );
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const copyTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const panelId = useId();
  function cancelClose() {
    clearTimeout(closeTimer.current);
  }
  function show() {
    cancelClose();
    setOpen(true);
  }
  function hide() {
    cancelClose();
    setOpen(false);
  }
  useEffect(
    () => () => {
      clearTimeout(closeTimer.current);
      clearTimeout(copyTimer.current);
    },
    [],
  );
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        clearTimeout(closeTimer.current);
        setOpen(false);
      }
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      trigger.current?.focus();
      clearTimeout(closeTimer.current);
      setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  async function copyWallet() {
    if (!account.wallet) return;
    clearTimeout(copyTimer.current);
    try {
      await navigator.clipboard.writeText(account.wallet);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
    copyTimer.current = setTimeout(() => setCopyState('idle'), 2500);
  }
  return (
    <div
      className="account-menu"
      ref={root}
      onPointerEnter={(event) => {
        if (event.pointerType !== 'touch') show();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'touch') return;
        cancelClose();
        closeTimer.current = setTimeout(() => setOpen(false), 180);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          hide();
      }}
    >
      <button
        ref={trigger}
        type="button"
        className="avatar-button"
        aria-label="Account menu"
        aria-expanded={open}
        aria-controls={panelId}
        onFocus={(event) => {
          if (event.currentTarget.matches(':focus-visible')) show();
        }}
        onClick={(event) => {
          cancelClose();
          setOpen((previous) => event.detail === 0 || !previous);
        }}
      >
        <ProfileGlyph />
      </button>
      {open && (
        <div className="account-menu-popover" id={panelId}>
          <section className="account-menu-panel" aria-label="Account">
            <div className="account-menu-heading">
              <span>Account</span>
              {account.identityLabel && <small>{account.identityLabel}</small>}
            </div>
            <div className="account-menu-wallet">
              <div className="account-menu-wallet-label">
                <Wallet size={15} /> Privy EVM wallet
              </div>
              {account.wallet ? (
                <>
                  <button
                    type="button"
                    className="account-menu-copy"
                    onClick={() => void copyWallet()}
                    aria-label="Copy wallet address"
                    title={account.wallet}
                  >
                    <span>
                      {account.wallet.slice(0, 6)}…{account.wallet.slice(-4)}
                    </span>
                    {copyState === 'copied' ? (
                      <Check size={16} />
                    ) : (
                      <Copy size={16} />
                    )}
                  </button>
                  <span className="account-menu-copy-status" role="status">
                    {copyState === 'copied'
                      ? 'Address copied'
                      : copyState === 'error'
                        ? 'Select the address below to copy.'
                        : ''}
                  </span>
                  {copyState === 'error' && (
                    <code className="account-menu-full-address">
                      {account.wallet}
                    </code>
                  )}
                </>
              ) : (
                <p className="account-menu-wallet-missing">
                  Sign in again to create your wallet.
                </p>
              )}
            </div>
            <div className="account-menu-divider" />
            <button
              type="button"
              className="account-menu-item account-menu-logout"
              onClick={() => {
                hide();
                account.logout();
              }}
            >
              <LogOut size={18} /> Sign out
            </button>
          </section>
        </div>
      )}
    </div>
  );
}
